import './ActivityModal.scss';

import { useState } from 'react';
import moment from 'moment';
import { Tooltip, Table, Divider, Row, Col, Modal, Alert, Empty } from 'antd';
import { useQuery } from '@apollo/client';

import { getProposalTitle } from 'helpers/proposal';
import Loader from 'components/Loader';
import PublishContent from 'pages/Proposal/components/Publish/components/PublishContent';
import { GET_CONFIGURATION } from 'graphql/queries/configurationQueries';
import { GET_SINGLE_PROPOSAL } from 'graphql/queries/proposalQueries';
import CloseIcon from 'components/Icons/CloseIcon';
import { DEFAULT_HEATMAP } from 'constants/index';

export const ActivityModal = ({ proposal, user, onCancel }) => {
  const [activeTab, setActiveTab] = useState(0);

  const tabs = ['Proposal Link', 'Emails', 'Heatmap'];
  const className = `open-logs-modal ${activeTab !== 0 ? 'no-footer' : ''}`;

  return (
    <Modal
      className={`confirm-modal ${className}`}
      centered
      visible={true}
      onCancel={onCancel}
      closeIcon={<CloseIcon className="close-icon" />}
      footer={null}>
      <h3 className="title">Activity</h3>
      <div className="divider" />

      <div className="body">
        <Row>
          <Col span={14}>
            <div className="content title-text">{getProposalTitle({ proposal, user })}</div>
          </Col>
          <Col span={10}>
            <Row className="activity-tabs">
              {tabs.map((t, index) => (
                <span
                  key={index}
                  className={`tab ${index === activeTab ? 'active' : ''}`}
                  onClick={() => setActiveTab(index)}>
                  {t}
                </span>
              ))}
            </Row>
          </Col>
        </Row>
        {activeTab === 0 && <RenderWatchedTable {...{ proposal }} />}
        {activeTab === 1 && <RenderEmailTable {...{ proposal }} />}
        {activeTab === 2 && <HeatMap {...{ proposal, user }} />}
      </div>
    </Modal>
  );
};

// Page level timing was added after older proposals were sent, so their view logs carry
// `watched` but no `pageTime`. Detected from the data rather than a cutoff date, so a
// proposal shows its Heatmap as soon as it has any page data at all.
const NO_PAGE_DATA_MESSAGES = {
  legacy:
    'Page by page tracking was added after this proposal was viewed, so there is no heatmap data for those views. Any new views will be tracked.',
  neverViewed: 'This proposal has not been opened yet, so there is no heatmap data to show.',
};

const getNoPageDataReason = (fetchedProposal, pageHeatMap) => {
  if (Object.keys(pageHeatMap || {}).length > 0) {
    return null;
  }

  // `openLogs` also carries email events (type/event/recipient), so only entries with
  // `watched` represent an actual view
  const hasViews = (fetchedProposal?.openLogs || []).some((log) => log?.watched != null);

  return hasViews ? 'legacy' : 'neverViewed';
};

const HeatMap = ({ proposal, user }) => {
  const [config, setConfig] = useState(null);
  const [fetchedProposal, setFetchedProposal] = useState(null);
  const [pageHeatMap, setPageHeatMap] = useState([]);

  const { loading: loadingConfig } = useQuery(GET_CONFIGURATION, {
    fetchPolicy: 'cache-and-network',
    variables: {
      language: proposal?.language?.toLowerCase() || 'english',
    },
    onCompleted: (data) => {
      setConfig(data.fetchConfiguration);
    },
  });

  const { loading: loadingProposal } = useQuery(GET_SINGLE_PROPOSAL, {
    fetchPolicy: 'cache-and-network',
    variables: {
      pid: proposal?.pid,
    },
    onCompleted: (data) => {
      const { fetchProposal } = data;

      const heatMap = {};
      let totalTime = 0;
      for (const openLog of fetchProposal.openLogs) {
        for (const pageTime in openLog.pageTime) {
          totalTime += openLog.pageTime[pageTime];
          if (heatMap[pageTime]) {
            heatMap[pageTime].time += openLog.pageTime[pageTime];
            heatMap[pageTime].count += 1;
          } else {
            heatMap[pageTime] = {
              ...DEFAULT_HEATMAP,
              time: openLog.pageTime[pageTime],
              count: 1,
            };
          }
        }
      }
      for (const pageTime in heatMap) {
        heatMap[pageTime].percentage = (
          ((heatMap[pageTime]?.time || 0) / (totalTime > 0 ? totalTime : 1)) *
          100
        ).toFixed(2);
        heatMap[pageTime].minutes = Math.floor(((heatMap[pageTime]?.time || 0) * 1000) / 60000);
        heatMap[pageTime].seconds = Math.floor(
          (((heatMap[pageTime]?.time || 0) * 1000) % 60000) / 1000
        ).toFixed(0);
      }

      setPageHeatMap(heatMap);
      setFetchedProposal(fetchProposal);
    },
  });

  if (loadingConfig || loadingProposal || !config || !fetchedProposal) {
    return (
      <Row className="activity-modal-publish-wrap heatmap-loader-wrap" gutter={0}>
        <Loader />
      </Row>
    );
  }

  const noPageDataReason = getNoPageDataReason(fetchedProposal, pageHeatMap);

  return (
    <Row className="activity-modal-publish-wrap" gutter={0}>
      <Col span={24} className="activity-modal-col1">
        {noPageDataReason && (
          <Alert
            className="heatmap-no-data-alert"
            type="info"
            showIcon
            message={NO_PAGE_DATA_MESSAGES[noPageDataReason]}
          />
        )}

        <PublishContent
          config={config}
          prop={fetchedProposal}
          user={user}
          fromActivityModal={true}
          path="/pd/:id"
          // an empty map still renders every page break with DEFAULT_HEATMAP, i.e. 0
          // views / 0 secs — the notice above explains why they are all zero
          heatmaps={pageHeatMap}
        />
      </Col>
    </Row>
  );
};

const RenderWatchedTable = ({ proposal }) => {
  const proposalLinkColumns = [
    {
      title: '#',
      dataIndex: 'index',
      key: 'index',
      className: 'index-column',
      render: (_, __, index) => index + 1,
    },
    {
      title: 'Opened',
      dataIndex: 'date',
      key: 'date',
      render: (date) => {
        if (date.length < 11) {
          // for 28-11-2022
          date = date.split('/').reverse().join('-');
          return <>{moment(date).format('MMM D, YYYY')}</>;
        }
        /* NEED this if showing invalid date for some
        else if (date.length < 17) {
          // for 28-11-2022 06:25
          date = date.split(' ');
          date[0] = date[0].split('/').reverse().join('-')
          date = date.join(' ')
        }
        */
        return <>{moment(date).format('MMM D, YYYY HH:mm')}</>;
      },
    },
    {
      title: 'Watched',
      dataIndex: 'watched',
      key: 'watched',
      render: (watched) => {
        let minutes = watched ? `${Math.floor((watched * 1000) / 60000)}` : null;
        let seconds = watched ? `${(((watched * 1000) % 60000) / 1000).toFixed(0)}` : null;

        if (minutes?.length < 2) minutes = `0${minutes}`;
        if (seconds?.length < 2 && seconds > 10) seconds = `${seconds}0`;
        else if (seconds && seconds < 10) seconds = `0${seconds}`;

        return (
          <>
            {minutes || '00'}:{seconds || '00'} <span className="open_logs_span">min</span>
          </>
        );
      },
    },
    {
      title: 'Device',
      dataIndex: 'deviceInfo',
      key: 'deviceInfo',
      render: (deviceInfo) => (
        <Tooltip
          overlayClassName="device-info-tooltip"
          title={
            deviceInfo ? (
              <div className="device-info-container">
                <Row>
                  <Col>{deviceInfo?.deviceName}</Col>
                  <Divider type="vertical" />
                  <Col>{deviceInfo?.deviceType}</Col>
                </Row>
                <Row>{deviceInfo.deviceAgent}</Row>
              </div>
            ) : null
          }>
          {deviceInfo?.deviceType}
        </Tooltip>
      ),
    },
    {
      title: 'Browser',
      dataIndex: 'deviceBrowser',
      key: 'deviceInfo',
      render: (_, { deviceInfo }) => <>{deviceInfo?.deviceBrowser || ''}</>,
    },
    {
      title: 'IP',
      dataIndex: 'clientIP',
      key: 'clientIP',
      render: (clientIP) => <>{clientIP.split('-')[0] || ''}</>,
    },
  ];

  return (
    <Table
      rowKey={'index'}
      columns={proposalLinkColumns}
      dataSource={proposal?.openLogs || []}
      pagination={{
        pageSize: 5,
      }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description="This proposal has not been opened yet."
          />
        ),
      }}
    />
  );
};

const RenderEmailTable = ({ proposal }) => {
  const emailColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: '160px',
      render: (date) => {
        if (date.length < 11) {
          // for 28-11-2022
          date = date.split('/').reverse().join('-');
          return <>{moment(date).format('MMM D, YYYY')}</>;
        }
        /* NEED this if showing invalid date for some
        else if (date.length < 17) {
          // for 28-11-2022 06:25
          date = date.split(' ');
          date[0] = date[0].split('/').reverse().join('-')
          date = date.join(' ')
        }
        */
        return <>{moment(date).format('MMM D, YYYY HH:mm')}</>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'event',
      width: '70px',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      width: '140px',
    },
    {
      title: 'Recipient',
      dataIndex: 'recipient',
      key: 'recipient',
      width: '100px',
      render: (_, item) => {
        return item.recipient || '-';
      },
    },
  ];

  return (
    <Table
      rowKey={'index'}
      columns={emailColumns}
      // copy before reversing: `reverse()` mutates in place, so reusing the prop array
      // would flip the order again on every re-render
      dataSource={[...(proposal?.emailLogs || [])].reverse()}
      pagination={{ pageSize: 5 }}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            // `recipients` is an array, so an empty one is still truthy - check the
            // length to tell "nobody to send to" apart from "sent nothing yet"
            description={
              proposal?.emailSettings?.recipients?.length
                ? 'No Data.'
                : 'Proposal email sending was not enabled for this proposal.'
            }
          />
        ),
      }}
    />
  );
};
